import {
  DeviceType,
  IOrganisationUser,
  IOrganisationUserDAO,
  IOrganisationUserReport,
  IOrganisationUserSignin,
  UserStatus,
} from 'types/domain/user';
import {
  CheckBadgeIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  ComputerDesktopIcon,
  DevicePhoneMobileIcon,
  DeviceTabletIcon,
  PencilIcon,
  PencilSquareIcon,
  TrashIcon,
  UserGroupIcon,
  UserIcon,
} from '@heroicons/react/24/outline';
import { Role } from 'types/domain/role';
import { convertTimeAgo } from 'utils/timeAgo';
import { convertDate } from 'utils/convertDate';
import { useAppDispatch } from 'core/store/hooks';
import { update } from 'core/actions/admin/data';
import { EntityTypes } from 'constants/entities';
import { Row } from '@tanstack/react-table';
import { CourseProgressStatus } from 'types/domain/permissions';
import { IGroup } from 'types/domain/group';
import { useCallback } from 'react';

export function DraggableNameBlock({ user }: { user: IOrganisationUserDAO }) {
  const onDragStart = (event, role, id) => {
    event.dataTransfer.setData(role, id);
    event.dataTransfer.effectAllowed = 'move';
  };

  if (!user) return <div className="text-gray-500">unknown</div>;

  return (
    <div
      draggable
      onDragStart={e => onDragStart(e, user.role, user.id)}
      className="flex items-center whitespace-nowrap py-2 pr-3 text-sm pl-4"
    >
      <div className="h-10 w-10 flex-shrink-0">{getIcon(user)}</div>
      <div className="ml-4">
        <div className="font-medium">
          {user.firstName} {user.lastName}
        </div>
        <div className="opacity-50">{user.email}</div>
      </div>
    </div>
  );
}

export function NameBlock({
  user,
  group,
  edit,
}: {
  user: IOrganisationUserDAO | IOrganisationUserReport | any;
  group?: IGroup;
  edit?: (value) => void;
}) {
  const onEdit = useCallback(
    e => {
      e.stopPropagation();
      edit(user.id);
    },
    [edit, user?.id],
  );

  if (user)
    return (
      <div className="flex items-center whitespace-nowrap py-2 pr-3">
        <div className="flex flex-col">
          <div className="font-medium flex items-center">
            {user.firstName} {user.lastName}
            {edit && user && (
              <PencilSquareIcon
                onClick={onEdit}
                className="ml-auto w-4 h-4 text-gray-400 hover:text-gray-700 cursor-pointer"
              />
            )}
          </div>
          {user.systemId && <div className="opacity-50 truncate">{user.systemId}</div>}
          {user.email && <div className="opacity-50 truncate">{user.email}</div>}

          {!user.firstName && !user.email && !user.systemId && <span>unknown</span>}
          {user.signin && (
            <div className="flex mr-1 justify-start items-center dark:text-slate-200  text-sm">
              <span className="h-4 w-4 mr-2">
                {user.signin.lastLoginSignin?.deviceType === DeviceType.DESKTOP && <ComputerDesktopIcon />}
                {user.signin.lastLoginSignin?.deviceType === DeviceType.MOBILE && <DevicePhoneMobileIcon />}
                {user.signin.lastLoginSignin?.deviceType === DeviceType.TABLET && <DeviceTabletIcon />}
              </span>
              <span>
                {user.signin.lastLoginSignin?.city} {user.signin.lastLoginSignin?.countryCode}
              </span>
            </div>
          )}
          {group && (
            <span className="flex justify-start items-center  text-sm dark:text-slate-200">
              <UserGroupIcon className="w-4 h-4 mr-1" />
              {group?.name}
            </span>
          )}
        </div>
      </div>
    );
}

export function DateBlock({ date, time }: { date: Date; time?: boolean }) {
  if (!date) return null;

  return (
    <div className="flex items-center flex-wrap py-4 pr-3 text-sm sm:pl-6">
      <span className="mr-3">{date.toLocaleDateString()}</span>
      {time && <span>{date.toLocaleTimeString()}</span>}
    </div>
  );
}

export function Expander({ row }: { row: Row<IOrganisationUserDAO> }) {
  return row.getCanExpand() ? (
    <button
      {...{
        onClick: row.getToggleExpandedHandler(),
        style: { cursor: 'pointer' },
      }}
    >
      {row.getIsExpanded() ? <ChevronDownIcon className="w-4 h-4" /> : <ChevronRightIcon className="w-4 h-4" />}
    </button>
  ) : null;
}

export function Status({ user }: { user: IOrganisationUserDAO }) {
  return (
    <>
      <span className="inline-flex rounded-full bg-green-100 px-2 text-xs font-semibold leading-5 text-green-800">
        Active
      </span>
    </>
  );
}

export function getIcon(p: IOrganisationUserDAO) {
  if (p?.avatar) {
    return <img className="inline-block h-10 w-10 rounded-full" src={p?.avatar} alt="" />;
  }

  var i = 'U';
  if (p?.email) i = Array.from(p?.email)[0].toUpperCase();
  var b = 'h-10 w-10 rounded-full flex justify-center items-center text-lg ';
  if (p?.type > 0) {
    b += ' bg-slate-700 text-slate-500';
  } else {
    b += ' bg-slate-700 text-slate-500';
  }

  if (i == 'U')
    return (
      <div className={b}>
        <UserIcon className="w-6 h-6"></UserIcon>
      </div>
    );
  // bg-gray-100 text-gray-800
  return <div className={b}>{i}</div>;
}

export function SignIn({ organisationUser }: { organisationUser: IOrganisationUser }) {
  if (!organisationUser) return null;

  return getLastSignin(organisationUser.signin, organisationUser.status);
}

export function getLastSignin(p: IOrganisationUserSignin, status) {
  if (status === UserStatus.INACTIVE)
    return (
      <div className="inline-flex rounded-md bg-slate-100 dark:bg-slate-800 dark:text-gray-300 px-5 py-3 text-sm font-semibold leading-5 text-gray-800">
        Inactive
      </div>
    );

  if (status === UserStatus.DELETED)
    return (
      <div className="inline-flex rounded-md bg-slate-100 dark:bg-slate-800 dark:text-gray-300 bg-red-50 px-5 py-3 text-sm font-semibold leading-5 text-red-800">
        Deleted
      </div>
    );

  if (p == null)
    return (
      <div className="inline-flex rounded-md bg-slate-100 dark:bg-slate-800 dark:text-gray-300 bg-red-50 px-5 py-2 text-xs font-semibold leading-5 text-red-800">
        Never Signed In
      </div>
    );

  return (
    <div className="inline-flex bg-slate-100 dark:bg-slate-800 dark:text-gray-300  rounded-md px-4 py-1 text-xs leading-5 text-gray-600">
      <span className="inline-block  pt-1 h-7 w-7 mr-2">
        <span className="h-6 w-6 pt-2">
          {p.lastLoginSignin?.deviceType === DeviceType.DESKTOP && <ComputerDesktopIcon />}
          {p.lastLoginSignin?.deviceType === DeviceType.MOBILE && <DevicePhoneMobileIcon />}
          {p.lastLoginSignin?.deviceType === DeviceType.TABLET && <DeviceTabletIcon />}
        </span>
      </span>
      <span className="flex flex-col text-xs">
        <span className="uppercase">
          <b>{convertTimeAgo(convertDate(p.lastLoginSignin?.created?.date))}</b>
        </span>
        <span>
          {p.lastLoginSignin?.browser} {p.lastLoginSignin?.os}
        </span>
        <span>
          {p.lastLoginSignin.city} {p.lastLoginSignin.countryCode}
        </span>
      </span>
    </div>
  );
}

export function RoleBlock({ user }: { user: IOrganisationUserDAO }) {
  return getRole(user);
}

function getRole(p: IOrganisationUserDAO) {
  if (p?.role === Role.SUPER_ADMIN || p?.role === Role.ADMIN) {
    return (
      <span className="inline-flex rounded-full bg-indigo-100 px-2 text-xs font-semibold leading-5 text-indigo-800">
        Admin
      </span>
    );
  }
  // bg-gray-100 text-gray-800
  return (
    <span className="inline-flex rounded-full px-2 text-xs font-semibold leading-5 bg-gray-100 text-gray-800">
      Learner
    </span>
  );
}

export function EditUser({ user, onRowClick }: { user: IOrganisationUserDAO; onRowClick: any }) {
  //const onEdit = e => {onRowClick(e,user)};

  return (
    <div
      onClick={e => onRowClick(user)}
      className="rounded-full hover:bg-gray-800 hover:ring-2 hover:ring-brand border border-gray-300 shadow-sm pt-2 w-10 h-10 flex justify-center cursor-pointer"
    >
      <PencilIcon className="w-5 h-5" />
    </div>
  );
}

export function RemoveGroupUser({ user }: { user: IOrganisationUserDAO }) {
  const dispatch = useAppDispatch();

  const onDelete = e =>
    dispatch(update({ type: EntityTypes.GROUP_USER, entity: { id: user.groupUserId, status: -1 } }));

  return (
    <div
      onClick={onDelete}
      className="rounded-full mr-2 hover:bg-gray-100 hover:ring-2 hover:ring-brand border border-gray-300 shadow-sm pt-2 w-10 h-10 flex justify-center cursor-pointer"
    >
      <TrashIcon className="w-5 h-5 hover:text-brand" />
    </div>
  );
}

export function LastAccessPermission({ row }: { row: Row<IOrganisationUserReport> }) {
  return row.original.lastAccessPermission ? (
    <div className="flex flex-col text-sm">
      <span className="font-bold">
        {row.original.lastAccessPermission.module?.text?.shortText || row.original.lastAccessPermission.module?.name}
      </span>
      <span className="text-gray-400">
        {row.original.lastAccessPermission.status === CourseProgressStatus.COMPLETE ? 'complete' : 'in progress'}
      </span>
    </div>
  ) : null;
}

export function LastCertificate({ row }: { row: Row<IOrganisationUserReport> }) {
  return row.original.lastCertificate ? (
    <div className="flex flex-col text-sm">
      <span className="font-bold text-bold">{row.original.lastCertificate.certificate.name}</span>
      <span className="">
        {row.original.lastCertificate.progress === 1 ? (
          <CheckBadgeIcon className="w-4 h-4 text-brand" />
        ) : (
          `${(row.original.lastCertificate.progress * 100).toFixed(0)}%`
        )}
      </span>
    </div>
  ) : null;
}
